import type { RaceRegistration } from "../api/types";

/**
 * The race detail endpoint does not currently echo the caller's registration.
 * Cache the server's successful response locally so a page refresh does not
 * turn a registered rider into an unregistered one. The cache is only ever
 * written after a successful HTTP registration and cleared on withdrawal; it
 * is a convenience, never a substitute for the server's answer.
 *
 * Entries are keyed by user id so a shared browser does not leak one rider's
 * pen into another rider's session.
 */

const PREFIX = "cimara.registration.";

function key(userId: string | null | undefined, eventId: string): string {
  return `${PREFIX}${userId ?? "anonymous"}.${eventId}`;
}

export function readCachedRegistration(
  userId: string | null | undefined,
  eventId: string,
): RaceRegistration | null {
  try {
    const raw = window.localStorage.getItem(key(userId, eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RaceRegistration>;
    if (
      typeof parsed.registrationId !== "string" ||
      typeof parsed.eventId !== "string" ||
      (parsed.category !== "A" &&
        parsed.category !== "B" &&
        parsed.category !== "C" &&
        parsed.category !== "D") ||
      typeof parsed.pen !== "number"
    ) {
      return null;
    }
    return {
      registrationId: parsed.registrationId,
      eventId: parsed.eventId,
      category: parsed.category,
      pen: parsed.pen,
      ...(typeof parsed.riderId === "number" ? { riderId: parsed.riderId } : {}),
      ...(typeof parsed.name === "string" ? { name: parsed.name } : {}),
    };
  } catch {
    return null;
  }
}

export function saveCachedRegistration(
  userId: string | null | undefined,
  registration: RaceRegistration,
): void {
  try {
    window.localStorage.setItem(
      key(userId, registration.eventId),
      JSON.stringify(registration),
    );
  } catch {
    // Private mode or a full quota: the in-memory registration still works.
  }
}

export function clearCachedRegistration(
  userId: string | null | undefined,
  eventId: string,
): void {
  try {
    window.localStorage.removeItem(key(userId, eventId));
  } catch {
    // Nothing to clear.
  }
}
